"use strict";

function IndcomToDoList() {
  function organizeFacilityInspections(data) {
    const now = new Date();

    return data.map(function (datum) {
      const dueDate = DateTime.parseIsoDate(datum.indcomFacilityInspection.dueDate);

      return {
        id: datum.id,
        todoId: datum.indcomFacilityInspection.todoId,
        tableIcon: datum.tableIcon,
        facilityIdentifier: datum.facilityIdentifier,
        name: datum.facilityName,
        fullAddress: Misc.getFullMailingAddress(datum.address, datum.city, datum.state, datum.zip),
        displayFacilityType: datum.displayFacilityType,
        displayOwnerContact: _getDisplayFirstContactWithRole(datum.contacts, "primary-contact"),
        displayDueDate: DateTime.dateToGroupDisplayDate(dueDate),
        dueDate,
        isPastDue: dueDate && dueDate < now,
        canInspectFacility: datum.canInspectFacility,
        assignee: datum.indcomFacilityInspection.assignee,
      };
    });
  }

  function organizeFacilityReinspection(data) {
    const now = new Date();

    return data.map(function (datum) {
      const dueDate = datum.indcomFacilityReinspection.dueDate
        ? DateTime.parseIsoDate(datum.indcomFacilityReinspection.dueDate)
        : null;

      return {
        id: datum.id,
        todoId: datum.indcomFacilityReinspection.todoId,
        tableIcon: datum.tableIcon,
        facilityIdentifier: datum.facilityIdentifier,
        name: datum.facilityName,
        fullAddress: Misc.getFullMailingAddress(datum.address, datum.city, datum.state, datum.zip),
        displayFacilityType: datum.displayFacilityType,
        displayFindings: datum.indcomFacilityReinspection.additionalInfo.displayFindings,
        displayOwnerContact: _getDisplayFirstContactWithRole(datum.contacts, "primary-contact"),
        selfInspectionAction: datum.indcomFacilityReinspection.additionalInfo.selfInspectionAction,
        displayDueDate: dueDate ? DateTime.dateToGroupDisplayDate(dueDate) : dueDate,
        dueDate,
        isPastDue: dueDate && dueDate < now,
        canInspectFacility: datum.canInspectFacility,
        assignee: datum.indcomFacilityReinspection.assignee,
      };
    });
  }

  function organizeFacilityMaintenanceResponse(data) {
    const now = new Date();

    return data.map(function (datum) {
      const dueDate = datum.indcomFacilityMaintenanceResponse.dueDate
        ? DateTime.parseIsoDate(datum.indcomFacilityMaintenanceResponse.dueDate)
        : null;

      return {
        id: datum.id,
        todoId: datum.indcomFacilityMaintenanceResponse.todoId,
        tableIcon: datum.tableIcon,
        facilityIdentifier: datum.facilityIdentifier,
        name: datum.facilityName,
        fullAddress: Misc.getFullMailingAddress(datum.address, datum.city, datum.state, datum.zip),
        displayFacilityType: datum.displayFacilityType,
        displayFindings: datum.indcomFacilityMaintenanceResponse.additionalInfo.displayFindings,
        displayOwnerContact: _getDisplayFirstContactWithRole(datum.contacts, "primary-contact"),
        selfInspectionAction:
          datum.indcomFacilityMaintenanceResponse.additionalInfo.selfInspectionAction,
        hasSecretCode: datum.indcomFacilityMaintenanceResponse.additionalInfo.hasSecretCode,
        displayDueDate: dueDate ? DateTime.dateToGroupDisplayDate(dueDate) : dueDate,
        dueDate,
        isPastDue: dueDate && dueDate < now,
        canInspectFacility: datum.canInspectFacility,
        assignee: datum.indcomFacilityMaintenanceResponse.assignee,
      };
    });
  }

  function organizeFacilityReviewResponse(data) {
    const now = new Date();

    return data.map(function (datum) {
      const dueDate = datum.indcomFacilityReviewResponse.dueDate
        ? DateTime.parseIsoDate(datum.indcomFacilityReviewResponse.dueDate)
        : null;

      return {
        id: datum.id,
        todoId: datum.indcomFacilityReviewResponse.todoId,
        tableIcon: datum.tableIcon,
        facilityIdentifier: datum.facilityIdentifier,
        name: datum.facilityName,
        fullAddress: Misc.getFullMailingAddress(datum.address, datum.city, datum.state, datum.zip),
        displayFacilityType: datum.displayFacilityType,
        displayFindings: datum.indcomFacilityReviewResponse.additionalInfo.displayFindings,
        displayOwnerContact: _getDisplayFirstContactWithRole(datum.contacts, "primary-contact"),
        displayDueDate: dueDate ? DateTime.dateToGroupDisplayDate(dueDate) : dueDate,
        dueDate,
        isPastDue: dueDate && dueDate < now,
        canInspectFacility: datum.canInspectFacility,
        assignee: datum.indcomFacilityReviewResponse.assignee,
      };
    });
  }

  function organizeFacilityEnforcements(data) {
    const result = [];

    for (const datum of data) {
      for (const enforcement of datum.indcomFacilityEnforcement.additionalInfo) {
        const issueDate = enforcement.issueDate
          ? DateTime.parseIsoDate(enforcement.issueDate)
          : null;

        if (!enforcement.currentEscalation) continue;

        result.push({
          id: datum.id,
          todoId: datum.indcomFacilityEnforcement.todoId,
          enforcementId: enforcement.id,
          tableIcon: datum.tableIcon,
          facilityIdentifier: datum.facilityIdentifier,
          name: datum.facilityName,
          fullAddress: Misc.getFullMailingAddress(
            datum.address,
            datum.city,
            datum.state,
            datum.zip,
          ),
          displayResponsiblePartyManagement: Misc.getOptionNameByValue(
            IndcomInventoryConstants.responsiblePartyOptions,
            datum.responsiblePartyManagement,
          ),
          displayFacilityType: datum.displayFacilityType,
          displayEnforcementReason: Misc.getOptionNameByValue(
            EnforcementAction.getEnforcementReasonOptions(),
            enforcement.currentEscalation?.enforcementReason,
          ),
          displayLevel: Misc.getOptionNameByValue(
            FormSettings.getDropdownOptions("level"),
            enforcement.currentEscalation?.level,
          ),
          fine: enforcement.currentEscalation?.fine,
          issueDate: issueDate,
          displayIssueDate: issueDate ? DateTime.dateToGroupDisplayDate(issueDate) : issueDate,
          canIssueEnforcement: datum.canIssueEnforcement,
          assignee: datum.indcomFacilityEnforcement.assignee,
        });
      }
    }

    return result;
  }

  function organizeFacilityFlaggedForNewEnforcement(data) {
    return data.map(function (datum) {
      return {
        id: datum.id,
        facilityId: datum.id,
        tableIcon: datum.tableIcon,
        facilityIdentifier: datum.facilityIdentifier,
        name: datum.facilityName,
        fullAddress: Misc.getFullMailingAddress(datum.address, datum.city, datum.state, datum.zip),
        displayResponsiblePartyManagement: Misc.getOptionNameByValue(
          IndcomInventoryConstants.responsiblePartyOptions,
          datum.responsiblePartyManagement,
        ),
        canIssueEnforcement: datum.canIssueEnforcement,
        todoId: datum.indcomFacilityFlaggedForNewEnforcement.todoId,
        assignee: ToDoFunctions.getAssignee(datum.indcomFacilityFlaggedForNewEnforcement),
      };
    });
  }

  function organizeFacilityRecommendedAction(data) {
    const result = [];
    for (const datum of data) {
      for (const enforcement of datum.indcomFacilityRecommendedAction.additionalInfo) {
        const issueDate = enforcement.issueDate
          ? DateTime.parseIsoDate(enforcement.issueDate)
          : null;

        if (!enforcement.currentEscalation) continue;

        result.push({
          id: datum.id,
          enforcementId: enforcement.id,
          tableIcon: datum.tableIcon,
          facilityIdentifier: datum.facilityIdentifier,
          name: datum.facilityName,
          fullAddress: Misc.getFullMailingAddress(
            datum.address,
            datum.city,
            datum.state,
            datum.zip,
          ),
          displayResponsiblePartyManagement: Misc.getOptionNameByValue(
            IndcomInventoryConstants.responsiblePartyOptions,
            datum.responsiblePartyManagement,
          ),
          displayFacilityType: datum.displayFacilityType,
          displayEnforcementReason: Misc.getOptionNameByValue(
            EnforcementAction.getEnforcementReasonOptions(),
            enforcement.currentEscalation?.enforcementReason,
          ),
          displayLevel: Misc.getOptionNameByValue(
            FormSettings.getDropdownOptions("level"),
            enforcement.currentEscalation?.level,
          ),
          fine: enforcement.currentEscalation?.fine,
          issueDate: issueDate,
          displayIssueDate: issueDate ? DateTime.dateToGroupDisplayDate(issueDate) : issueDate,
          canIssueEnforcement: datum.canIssueEnforcement,
          displayRecommendedAction: BaseEnforcementAction().escalationActionOptions.find(
            (option) => option.value === enforcement.recommendedAction,
          )?.name,
          recommendedAction: enforcement.recommendedAction,
          todoId: datum.indcomFacilityRecommendedAction.todoId,
          assignee: ToDoFunctions.getAssignee(datum.indcomFacilityRecommendedAction),
        });
      }
    }

    return result;
  }

  function organizeFacilityInventoryIncomplete(data) {
    const currentUser = Session.getCurrentUserObject();
    return data.map(function (datum) {
      return {
        id: datum.id,
        todoId: datum.indcomFacilityInventoryIncomplete.todoId,
        tableIcon: datum.tableIcon,
        facilityIdentifier: datum.facilityIdentifier,
        name: datum.facilityName,
        fullAddress: Misc.getFullMailingAddress(datum.address, datum.city, datum.state, datum.zip),
        displayResponsiblePartyManagement: Misc.getOptionNameByValue(
          IndcomInventoryConstants.responsiblePartyOptions,
          datum.responsiblePartyManagement,
        ),
        displayFacilityType: datum.displayFacilityType,
        displayInventoryIncomplete: datum.indcomFacilityInventoryIncomplete.additionalInfo
          .map(_getRequiredFieldDisplayName)
          .sort(SortUtilities.alphaSorter),
        canEditFacility: canEditFacility(currentUser),
        assignee: datum.indcomFacilityInventoryIncomplete.assignee,
      };
    });
  }

  function canReviewFacility(user) {
    return (
      canInspectFacility(user) &&
      ToolSettings.getSetting("group", "portalSubscriberTools")?.includes("indcom")
    );
  }

  function canInspectFacility(user) {
    return user.permissions.can_inspect_facility;
  }

  function canEnforceFacility(user) {
    return user.permissions.can_issue_facility_enforcement;
  }

  function canEditFacility(user) {
    return user.permissions.can_edit_facility;
  }

  function getSortersDict() {
    const dateSorter = DateTime.dateComparator;
    const alphaSorter = SortUtilities.alphaSorter;
    const numberSorter = SortUtilities.numberSorter;
    const alphaArrayQuantitySort = (a, b) => SortUtilities.arrayQuantitySort(a, b, alphaSorter);

    return {
      assignee: ToDoUserPicker.assigneeSort,
      name: alphaSorter,
      facilityIdentifier: alphaSorter,
      fullAddress: alphaSorter,
      displayResponsiblePartyManagement: alphaSorter,
      displayFacilityType: alphaSorter,
      displayOwnerContact: alphaSorter,
      dueDate: dateSorter,
      displayFindings: alphaArrayQuantitySort,
      displayFollowUpInspectionType: alphaSorter,
      displayEnforcementReason: alphaSorter,
      displayLevel: alphaSorter,
      fine: numberSorter,
      issueDate: dateSorter,
      displayInventoryIncomplete: alphaArrayQuantitySort,
    };
  }

  function loadListeners($container) {
    $container
      .off("click", ".add-facility-inspection", _onAddFacilityInspection)
      .on("click", ".add-facility-inspection", _onAddFacilityInspection)
      .off("click", ".add-facility-self-inspection", _onAddFacilitySelfInspection)
      .on("click", ".add-facility-self-inspection", _onAddFacilitySelfInspection)
      .off("click", ".show-facility-inventory", _onShowFacilityInventory)
      .on("click", ".show-facility-inventory", _onShowFacilityInventory);
  }

  function loadFlaggedForEnforcementListeners($container, { selectedTable }) {
    const onFlaggedInspectionsClear = function (e) {
      e.stopPropagation();
      const checkedRowIds = selectedTable
        .getAllSelectedRows()
        .toArray()
        .map((elm) => $(elm).data("id"));

      IndcomToDoController.clearFlaggedInspections(checkedRowIds);
    };

    $container.off("click", ".js-clear-flagged-inspections", onFlaggedInspectionsClear);
    $container.on("click", ".js-clear-flagged-inspections", onFlaggedInspectionsClear);
  }

  function _onAddFacilityInspection() {
    const id = $(this).parents("tr").data("id");
    const inspectionType = $(this).data("inspection-type");
    const defaultData = {};

    if (inspectionType) {
      defaultData.inspectionType = inspectionType;
    }

    FacilityInspectionController.loadNewFacilityInspection(id, { defaultData });
  }

  function _onAddFacilitySelfInspection() {
    const id = $(this).parents("tr").data("id");

    FacilitySelfInspectionController.loadNew(id);
  }

  function _onShowFacilityInventory() {
    const id = $(this).parents("tr").data("id");

    IndcomInventoryModalController.invokeIndcomInventoryModal(id);
  }

  function _getDisplayFirstContactWithRole(contacts, role) {
    const owner = contacts.find((contact) => contact.displayRole === role);

    if (!owner) {
      return null;
    }

    const displayParts = [];

    if (owner.name) {
      displayParts.push(owner.name);
    }

    if (owner.phoneNumber) {
      displayParts.push(`(${owner.phoneNumber})`);
    }

    return displayParts.join(" ");
  }

  function _getRequiredFieldDisplayName(requiredField) {
    return Misc.getOptionNameByValue(IndcomInventoryConstants.requiredFieldOptions, requiredField);
  }

  return {
    organizeFacilityInspections,
    organizeFacilityReinspection,
    organizeFacilityMaintenanceResponse,
    organizeFacilityReviewResponse,
    organizeFacilityEnforcements,
    organizeFacilityRecommendedAction,
    organizeFacilityFlaggedForNewEnforcement,
    organizeFacilityInventoryIncomplete,
    canInspectFacility,
    canEnforceFacility,
    canEditFacility,
    canReviewFacility,
    getSortersDict,
    loadListeners,
    loadFlaggedForEnforcementListeners,
  };
}

module.exports = IndcomToDoList();

const DateTime = require("../dateTime");
const BaseEnforcementAction = require("../enforcement/baseEnforcementAction");
const SortUtilities = require("../general/sortUtilities");
const ToDoUserPicker = require("../general/toDoUserPicker");
const Misc = require("../misc");
const ToDoFunctions = require("../mapFunctions/toDoFunctions");
const FormSettings = require("../settings/formSettings");
const ToolSettings = require("../settings/toolSettings");
const EnforcementAction = require("./facilityEnforcement/enforcementAction");
const FacilityInspectionController = require("./facilityInspection/facilityInspectionController");
const FacilitySelfInspectionController = require("./facilitySelfInspection/facilitySelfInspectionController");
const IndcomInventoryConstants = require("./indcomInventoryConstants");
const IndcomInventoryModalController = require("./indcomInventoryModalController");
const IndcomToDoController = require("./indcomToDoController");
const Session = require("../login/session");
